import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";

export function BlogList() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.publicBlogList().then((r) => setPosts(r.posts)).finally(() => setLoading(false));
    }, []);

    return (
        <PageShell>
            <section className="max-w-4xl mx-auto px-6 py-16" data-testid="blog-list-page">
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Insights</div>
                <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>SwingTrust Journal</h1>
                <p className="mt-3 text-base" style={{ color: "var(--st-ink-muted)" }}>
                    Long-form commentary on trading discipline, market structure, and the education philosophy behind
                    SwingTrust.
                </p>

                <div className="mt-10 space-y-4">
                    {loading && <div className="text-sm">Loading…</div>}
                    {!loading && posts.length === 0 && (
                        <div className="st-surface p-8 text-center italic" style={{ color: "var(--st-silver-500)" }} data-testid="blog-empty-public">
                            No posts yet. The instructor is drafting the first essay.
                        </div>
                    )}
                    {posts.map((p) => (
                        <Link
                            key={p.id}
                            to={`/blog/${p.slug}`}
                            className="st-surface p-6 block transition-shadow hover:shadow-md"
                            data-testid={`public-blog-card-${p.slug}`}
                        >
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-royal)" }}>
                                {p.hero_eyebrow || "Insights"}
                            </div>
                            <h2 className="font-serif text-2xl mt-2" style={{ color: "var(--st-navy)" }}>{p.title}</h2>
                            {p.excerpt && (
                                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--st-ink-muted)" }}>{p.excerpt}</p>
                            )}
                            <div className="mt-4 text-xs" style={{ color: "var(--st-silver-500)" }}>
                                {new Date(p.published_at || p.created_at).toLocaleDateString()} · {p.author_name}
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </PageShell>
    );
}

export function BlogPost() {
    const slug = window.location.pathname.split("/").pop();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.publicBlogPost(slug).then(setPost).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <PageShell>
                <section className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="blog-not-found">
                    <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>Post not found.</h1>
                    <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        <Link to="/blog" className="link-royal">← Back to Journal</Link>
                    </p>
                </section>
            </PageShell>
        );
    }
    if (!post) {
        return <PageShell><div className="max-w-3xl mx-auto px-6 py-16 text-sm">Loading…</div></PageShell>;
    }

    return (
        <PageShell>
            <article className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-post-page">
                <div className="text-xs mb-4">
                    <Link to="/blog" className="link-royal" data-testid="blog-back-link">← Journal</Link>
                </div>
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    {post.hero_eyebrow || "Insights"}
                </div>
                <h1 className="font-serif text-4xl mt-2 leading-tight" style={{ color: "var(--st-navy)" }} data-testid="blog-post-title">
                    {post.title}
                </h1>
                <div className="mt-3 text-xs" style={{ color: "var(--st-silver-500)" }}>
                    {new Date(post.published_at || post.created_at).toLocaleDateString()} · {post.author_name}
                </div>
                <div
                    className="mt-8 space-y-4 text-base leading-relaxed whitespace-pre-wrap"
                    style={{ color: "var(--st-ink)" }}
                    data-testid="blog-post-body"
                >
                    {post.body_markdown}
                </div>
            </article>
        </PageShell>
    );
}
